import * as React from "react";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faXmark} from "@fortawesome/pro-regular-svg-icons";
import InlineBlockEditor from "./InlineBlockEditor";
import ImageBlockEditor from "./ImageBlockEditor";
import {EmailBlock, BLOCK_TYPES, isImageBlock} from "../../types/emailBlocks";

interface EmailTemplateImage {
    id: string;
    emailTemplateImageIdentifier: string;
    url: string;
    originalFilename: string;
    width: number;
    height: number;
    mediaType: string;
    fileSize: number;
}

interface BlockEditorPanelProps {
    block: EmailBlock | null;
    emailTemplateId: string;
    images: EmailTemplateImage[];
    onContentChange: (content: string) => void;
    onBlockChange: (updates: Partial<EmailBlock>) => void;
    onClose: () => void;
}

const BlockEditorPanel: React.FC<BlockEditorPanelProps> = ({
    block,
    emailTemplateId,
    images,
    onContentChange,
    onBlockChange,
    onClose,
}) => {
    const intl = useIntl();

    // Empty state when no block is selected
    if (!block) {
        return (
            <div className="flex-1 bg-gray-100 pt-6 px-4">
                <p className="text-gray-400 text-sm text-center">
                    {intl.formatMessage({id: "emailBlock.editor.empty"})}
                </p>
            </div>
        );
    }

    const blockConfig = BLOCK_TYPES.find((bt) => bt.type === block.type);
    const blockLabel = blockConfig
        ? intl.formatMessage({id: blockConfig.labelKey})
        : block.type;
    const isImage = isImageBlock(block.type);

    return (
        <div className="flex-1 flex flex-col min-h-0 bg-white">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 bg-red-100 text-red-600 rounded-sm">
                        {blockConfig?.icon && <FontAwesomeIcon icon={blockConfig.icon} className="w-3.5 h-3.5" />}
                    </span>
                    <span className="text-sm font-medium text-gray-700">{blockLabel}</span>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-sm"
                    title={intl.formatMessage({id: "button.close"})}
                >
                    <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-auto p-3">
                {isImage ? (
                    <ImageBlockEditor
                        key={block.id}
                        block={block}
                        emailTemplateId={emailTemplateId}
                        images={images}
                        onBlockChange={onBlockChange}
                    />
                ) : (
                    <InlineBlockEditor
                        key={block.id}
                        value={block.content}
                        onChange={onContentChange}
                        blockType={block.type}
                        placeholder={intl.formatMessage({
                            id: `emailBlock.placeholder.${block.type}`,
                        })}
                        autoFocus
                    />
                )}
            </div>
        </div>
    );
};

export default BlockEditorPanel;
